import { useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { isNative } from '@/lib/capacitor';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { useJobEditPermissions } from '@/hooks/useJobEditPermissions';
import {
    checkTapToPaySupport,
    ensureReaderWarm,
    hasCompletedTapToPayOnboarding,
    hasTapToPayLocationPermission,
    isReaderConnected,
    recordTapToPayDiagnostic,
} from '@/lib/tapToPay';

/**
 * Connects the Tap to Pay reader in the background so the payment screen doesn't
 * have to. SDK init, reader discovery and connect (plus Apple/Google attestation
 * and the connection-token round trip) add up to several seconds — far past the
 * 1s budget if they only start when the user asks to pay.
 *
 * Renders nothing, and every failure is silent: this is pure optimisation, and
 * the payment screen still does the full warm itself if this never ran.
 */

/**
 * Cap on how long we wait for the app to go idle before warming anyway. This
 * only ever applies to a device that is *still* busy — anything with spare
 * capacity fires long before it — so it can afford to be generous.
 */
const LAUNCH_WARM_IDLE_TIMEOUT_MS = 5000;
/** Fixed delay for webviews without requestIdleCallback (iOS before 17.4). */
const LAUNCH_WARM_FALLBACK_DELAY_MS = 2000;

/**
 * Run `task` once the app has spare capacity, or after `timeout` regardless.
 * Returns a cancel function.
 *
 * A fixed delay is wrong in both directions here. Too short and the warm
 * competes with OTA, auth and cache rehydration on a slow phone — making both
 * the app and the warm itself slower. Too long and a fast phone sits idle for no
 * reason, which is exactly the wait a user hits when they go straight to Tap to
 * Pay. Idleness is the signal we actually want, so ask for it.
 */
function whenIdle(task: () => void, timeout: number): () => void {
    const requestIdle = (window as any).requestIdleCallback;

    if (typeof requestIdle !== 'function') {
        const timerId = window.setTimeout(task, LAUNCH_WARM_FALLBACK_DELAY_MS);
        return () => window.clearTimeout(timerId);
    }

    const handle = requestIdle(task, { timeout });
    return () => (window as any).cancelIdleCallback?.(handle);
}
/**
 * Floor between background warms. Each one costs an attestation and a
 * connection-token call, so resuming the app repeatedly must not hammer either.
 */
const MIN_WARM_INTERVAL_MS = 10 * 60 * 1000;

let lastWarmStartedAt = 0;

async function warmIfEligible(resolveLocationId: () => Promise<string | undefined>): Promise<void> {
    if (Date.now() - lastWarmStartedAt < MIN_WARM_INTERVAL_MS) {
        return;
    }

    // Already connected — nothing to do, and re-warming would only risk
    // disturbing a healthy connection.
    if (await isReaderConnected()) {
        return;
    }

    const [support, onboarded, locationGranted] = await Promise.all([
        checkTapToPaySupport(),
        // The first connect on a device triggers Apple's Tap to Pay onboarding /
        // Terms sheet. That must only ever appear because the user asked to take
        // a payment, never unprompted at launch.
        hasCompletedTapToPayOnboarding(),
        hasTapToPayLocationPermission(),
    ]);

    if (!support.supported || !onboarded || !locationGranted) {
        recordTapToPayDiagnostic('warmup', 'Skipping background Tap to Pay warm', {
            supported: support.supported,
            onboarded,
            locationGranted,
        });
        return;
    }

    const locationId = await resolveLocationId();
    if (!locationId) {
        return;
    }

    lastWarmStartedAt = Date.now();
    const startedAt = Date.now();
    try {
        await ensureReaderWarm(locationId);
        recordTapToPayDiagnostic('warmup', 'Background Tap to Pay warm completed', {
            durationMs: Date.now() - startedAt,
        });
    } catch (err: any) {
        // Silent by design — the user has not asked for anything yet.
        recordTapToPayDiagnostic('warmup', 'Background Tap to Pay warm failed', {
            error: err?.message ?? String(err),
            durationMs: Date.now() - startedAt,
        }, 'warn');
    }
}

export function TapToPayWarmup() {
    const { isAuthenticated } = useAuth();
    const { selectedCompanyId } = useGlobalCompany();
    const { canUseTapToPay } = useJobEditPermissions();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!isNative() || !isAuthenticated || !selectedCompanyId || !canUseTapToPay) {
            return;
        }

        // Same key and staleTime as TakePaymentModal, so this usually reads the
        // cache instead of making a request.
        const resolveLocationId = async () => {
            try {
                const status = await queryClient.fetchQuery({
                    queryKey: ['paymentProviderStatus', selectedCompanyId],
                    queryFn: () => apiService.getPaymentProviderStatus(),
                    staleTime: 5 * 60 * 1000,
                });
                return status?.terminal_location_id ?? undefined;
            } catch {
                return undefined;
            }
        };

        const cancelIdleWarm = whenIdle(() => {
            void warmIfEligible(resolveLocationId);
        }, LAUNCH_WARM_IDLE_TIMEOUT_MS);

        // Reader sessions don't survive forever. On resume, reconnect only if the
        // native SDK says nothing is connected (checked inside warmIfEligible).
        const resumeListener = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
            if (isActive) {
                void warmIfEligible(resolveLocationId);
            }
        });

        return () => {
            cancelIdleWarm();
            void resumeListener.then((handle) => handle.remove());
        };
    }, [isAuthenticated, selectedCompanyId, canUseTapToPay, queryClient]);

    return null;
}
